import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  Fade,
  Button,
  IconButton,
  LinearProgress,
} from "@mui/material";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import Message from "./Message/Message";

const useStableDOMCallback = (
  targetRef: React.RefObject<Element>,
  onStable: () => void,
  delay: number = 200
) => {
  useEffect(() => {
    if (!targetRef.current) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(onStable, delay);
    });

    observer.observe(targetRef.current, { childList: true, subtree: true });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [targetRef, onStable, delay]);
};

interface ChatViewAreaProps {
  activeChatId: string | null;
  messages: any[];
  isGenerating: boolean;
  isLoadingMessages: boolean;
  hasMoreMessages: boolean;
  onFeedbackMessage?: (chatId: string, msgId: string) => void;
  onRelatedQuestionClick?: (question: string) => void;
  handleLoadMoreMessages: () => Promise<void>;
  sidebarOpen: boolean;
  models: { [key: string]: any };
  databases: { [key: string]: any };
  styles: { [key: string]: any };
  assistanceMode: string;
  onSendMessage: (content: string) => void;
}

const ChatViewArea: React.FC<ChatViewAreaProps> = ({
  activeChatId,
  messages,
  isGenerating,
  isLoadingMessages,
  hasMoreMessages,
  onFeedbackMessage = () => {},
  onRelatedQuestionClick = () => {},
  handleLoadMoreMessages,
  sidebarOpen,
  models,
  databases,
  styles,
  assistanceMode,
  onSendMessage,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showRegenerationButton, setShowRegenerationButton] = useState(false);
  const [copiedMessages, setCopiedMessages] = useState<Record<string, boolean>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    setIsInitialLoad(true);
    setShowScrollButton(false);
  }, [activeChatId]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  const handleInitialScroll = useCallback(() => {
    if (isInitialLoad && messages.length > 0) {
      scrollToBottom('auto');
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, messages.length, scrollToBottom]);

  useStableDOMCallback(messageContainerRef, handleInitialScroll);

  const handleCopyWithState = async (msgId: string, content: string) => {
    if (copiedMessages[msgId]) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessages((prev) => ({ ...prev, [msgId]: true }));
      setTimeout(() => {
        setCopiedMessages((prev) => ({ ...prev, [msgId]: false }));
      }, 5000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const messagesList = Array.isArray(messages) ? messages : [];

  const isAtBottom = useCallback(() => {
    if (!containerRef.current) return true;
    const { scrollHeight, scrollTop, clientHeight } = containerRef.current;
    return scrollHeight - scrollTop - clientHeight < 150;
  }, []);

  const handleScroll = useCallback(() => {
    setShowScrollButton(!isAtBottom());
  }, [isAtBottom]);

  useEffect(() => {
    if (isInitialLoad) {
      return;
    }

    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && hasMoreMessages && !isLoadingMessages) {
          const container = containerRef.current;
          if (container) {
            const previousScrollHeight = container.scrollHeight;
            await handleLoadMoreMessages();
            requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight - previousScrollHeight;
            });
          }
        }
      },
      { rootMargin: "100px", threshold: 0 }
    );
    
    const currentObserverRef = observerRef.current;
    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }
    
    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [isInitialLoad, hasMoreMessages, isLoadingMessages, handleLoadMoreMessages]);

  useEffect(() => {
    const lastMessage = messagesList[messagesList.length - 1];
    const wasUserOrIsGenerating = isGenerating || (lastMessage && lastMessage.sender === 'user');
    
    if (!isInitialLoad && wasUserOrIsGenerating && isAtBottom()) {
      setTimeout(() => scrollToBottom("smooth"), 100);
    }
  }, [messagesList, isGenerating, scrollToBottom, isAtBottom, isInitialLoad]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const regenerateMessage = () => {
    const lastUserMessage = [...messages].reverse().find((msg) => msg.sender === "user");
    if (lastUserMessage) {
      onSendMessage(lastUserMessage.content);
      setShowRegenerationButton(false);
    }
  };

  useEffect(() => {
    if (isGenerating) {
      setShowRegenerationButton(false);
      return;
    }
    const lastMessage = messages[messages.length - 1];
    if (!isGenerating && lastMessage && lastMessage.sender === "bot" && lastMessage.content.includes("ขออภัย")) {
      setShowRegenerationButton(true);
    } else {
      setShowRegenerationButton(false);
    }
  }, [messages, isGenerating]);

  return (
    <Box
      ref={containerRef}
      flex={1}
      p={2}
      sx={{
        position: 'relative',
        overflowY: "auto",
        scrollBehavior: "auto",
        paddingBottom: '160px',
        "&::-webkit-scrollbar": { width: "0.4em" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          background: `${alpha(theme.palette.text.primary, 0.15)}`,
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: `${alpha(theme.palette.text.primary, 0.3)}`,
        },
      }}
    >
      <div ref={observerRef} style={{ height: '1px' }}/>
      
      <div ref={messageContainerRef}>
        {messagesList.map((msg) => (
          <Message
            key={msg.id}
            msg={msg}
            chatId={activeChatId}
            theme={theme}
            copiedMessages={copiedMessages}
            onCopy={handleCopyWithState}
            onFeedbackMessage={onFeedbackMessage}
            onRelatedQuestionClick={onRelatedQuestionClick}
            models={models}
            databases={databases}
            styles={styles}
            isGenerating={isGenerating}
            assistanceMode={assistanceMode}
          />
        ))}
      </div>
      
      {isGenerating && (
        <Fade in={true} timeout={300}>
          <Box display="flex" justifyContent="flex-start" mb={1} px={{ xs: 1, sm: 2 }}>
            <Box sx={{ maxWidth: { xs: "100%", sm: "80%", md: "70%" } }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  backgroundColor: alpha(theme.palette.grey[800], 0.3),
                  borderRadius: '12px',
                  backdropFilter: 'blur(5px)',
                  border: `1px solid ${alpha(theme.palette.grey[700], 0.5)}`,
                  width: 'fit-content',
                }}
              >
                <Typography variant="body2" sx={{ color: "white" }}>
                  กำลังทำการประมวลผล กรุณารอสักครู่
                </Typography>

                <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
                  <Box
                    sx={{
                      "@keyframes bounce": {
                        "0%, 80%, 100%": {
                          transform: "scale(0)",
                        },
                        "40%": {
                          transform: "scale(1.0)",
                        },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      bgcolor: "white",
                      borderRadius: "50%",
                      animation: "bounce 1.4s infinite ease-in-out both",
                      animationDelay: "-0.32s",
                    }}
                  />
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      bgcolor: "white",
                      borderRadius: "50%",
                      animation: "bounce 1.4s infinite ease-in-out both",
                      animationDelay: "-0.16s",
                    }}
                  />
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      bgcolor: "white",
                      borderRadius: "50%",
                      animation: "bounce 1.4s infinite ease-in-out both",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      <div ref={messagesEndRef} />

      <Box
        sx={{
          position: "absolute",
          bottom: '160px',
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 2,
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        <Fade in={showScrollButton}>
          <IconButton
            onClick={() => scrollToBottom("smooth")}
            sx={{
              background: alpha(theme.palette.background.paper, 0.7),
              backdropFilter: 'blur(8px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              color: 'text.primary',
              "&:hover": { background: alpha(theme.palette.background.paper, 0.9) },
            }}
          >
            <ArrowDownwardOutlinedIcon />
          </IconButton>
        </Fade>

        <Fade in={showRegenerationButton}>
          <Button
            onClick={regenerateMessage}
            startIcon={<ReplayOutlinedIcon />}
            sx={{
              background: alpha(theme.palette.background.paper, 0.7),
              backdropFilter: 'blur(8px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              color: 'text.primary',
              "&:hover": { background: alpha(theme.palette.background.paper, 0.9) },
              borderRadius: 8,
              textTransform: "none",
            }}
          >
            Regenerate
          </Button>
        </Fade>
      </Box>
    </Box>
  );
};

export default ChatViewArea;